import { configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import { combineReducers } from 'redux';
import { authSlice, rtkQueryErrorLogger } from 'store/slices';
import {
  blockerModalsState,
  dashboardState,
  drawerState,
  heliosConditionsState,
  heliosNotificationConditionsState,
  modulesAndAgendasState,
  navigationState,
  tablesQueryState,
  templateFormsState,
  uiState,
} from './entities';
import { coreApi } from 'store/services/core';
import { documentStorageApi } from 'store/services/documentStorage';
import { flowApi } from 'store/services/flow';
import { heliosApi } from 'store/services/helios';
import { chatbotState } from './entities/chatbot';

const MAX_PERSIST_SIZE = 600 * 1024; // 600KB hard cap

const storage = {
  getItem: (key: string) => Promise.resolve(localStorage.getItem(key)),
  setItem: (key: string, item: string) => {
    if (item.length > MAX_PERSIST_SIZE) {
      return Promise.resolve();
    }
    return Promise.resolve(localStorage.setItem(key, item));
  },
  removeItem: (key: string) => Promise.resolve(localStorage.removeItem(key)),
};

const PERSIST_KEY = 'flowio-persist-v2';
const LEGACY_PERSIST_KEY = 'root';
const OLD_PERSIST_KEY = 'chatbox-storage';

const migratePersistedState = () => {
  if (typeof window === 'undefined') return;

  try {
    const nextKey = `persist:${PERSIST_KEY}`;
    const legacyKey = `persist:${LEGACY_PERSIST_KEY}`;
    const oldKey = `persist:${OLD_PERSIST_KEY}`;
    const nextValue = localStorage.getItem(nextKey);
    const legacyValue = localStorage.getItem(legacyKey);

    if (!nextValue && legacyValue) {
      localStorage.setItem(nextKey, legacyValue);

      if (localStorage.getItem(nextKey)) {
        localStorage.removeItem(legacyKey);
      }
    } else if (nextValue && legacyValue) {
      localStorage.removeItem(legacyKey);
    }

    localStorage.removeItem(oldKey);
  } catch {
    // Ignore storage migration errors and let redux-persist continue normally.
  }
};

migratePersistedState();

const persistConfig = {
  key: PERSIST_KEY,
  storage,
  whitelist: ['chatbot'],
  blacklist: [coreApi.reducerPath, documentStorageApi.reducerPath, flowApi.reducerPath, heliosApi.reducerPath],
  throttle: 2000,
};

const rootReducer = combineReducers({
  [coreApi.reducerPath]: coreApi.reducer,
  [documentStorageApi.reducerPath]: documentStorageApi.reducer,
  [flowApi.reducerPath]: flowApi.reducer,
  [heliosApi.reducerPath]: heliosApi.reducer,
  auth: authSlice.reducer,
  tablesQuery: tablesQueryState.reducer,
  modulesAndAgendas: modulesAndAgendasState.reducer,
  heliosTemplateForms: templateFormsState.reducer,
  navigationState: navigationState.reducer,
  dashboard: dashboardState.reducer,
  drawerState: drawerState.reducer,
  heliosConditions: heliosConditionsState.reducer,
  heliosNotificationConditions: heliosNotificationConditionsState.reducer,
  blockerModals: blockerModalsState.reducer,
  uiState: uiState.reducer,
  chatbot: chatbotState.reducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }).concat(coreApi.middleware, documentStorageApi.middleware, flowApi.middleware, heliosApi.middleware, rtkQueryErrorLogger),
  //   devTools: process.env.TYPE === 'local',
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
